/**
 * Settings Models and Interfaces
 * TypeScript interfaces for user profile, organization, team members, and preferences
 */

// Enums
export enum UserRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
  VIEWER = 'viewer'
}

export enum MemberStatus {
  ACTIVE = 'active',
  INVITED = 'invited',
  INACTIVE = 'inactive'
}

export enum NotificationType {
  EMAIL = 'email',
  WEBHOOK = 'webhook',
  SLACK = 'slack'
}

// User Profile
export interface UserProfileStats {
  documents_parsed: number;
  templates_created: number;
  api_keys_created: number;
  days_active: number;
}

export interface UserActivityItem {
  icon: 'document' | 'key' | 'template' | 'settings';
  title: string;
  description: string;
  occurred_at: string;
  color: 'blue' | 'green' | 'red' | 'orange';
}

export interface UserActivityResponse {
  activities: UserActivityItem[];
  stats: UserProfileStats;
  total?: number;
  page?: number;
  page_size?: number;
}

export interface UserActivityQuery {
  limit?: number;
  page?: number;
  page_size?: number;
  search?: string;
  type?: string;
}

export interface TeamMembersQuery {
  page: number;
  pageSize: number;
  search: string;
  role: string;
  status: string;
  sort: string;
}

export interface ActiveSessionItem {
  id: string;
  is_current: boolean;
  label: string;
  last_active?: string | null;
}

export interface ActiveSessionsResponse {
  sessions: ActiveSessionItem[];
  note: string;
}

export interface UserProfile {
  _id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  country?: string;
  organization_country?: string;
  job_title?: string;
  department?: string;
  bio?: string;
  avatar_url?: string;
  has_2fa_enabled?: boolean;
  two_factor_enabled?: boolean;
  client_2fa_available?: boolean | null;
  client_enforce_2fa?: boolean | null;
  two_factor_secret?: string;
  organization_id: string;
  role: UserRole;
  created_at: Date;
  updated_at: Date;
  last_login?: Date;
  stats?: UserProfileStats;
}

// Organization
export interface Organization {
  _id: string;
  name: string;
  logo_url?: string;
  owner_id: string;

  // Company Address
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country: string;

  // Tax Information
  tax_id?: string;

  // Timezone
  timezone: string;

  // Webhook settings
  webhook_url?: string;
  webhook_secret?: string;
  webhook_events?: string[];

  // Subscription
  subscription_tier: string;
  subscription_status: string;
  monthly_api_calls_limit: number;
  monthly_api_calls_used: number;
  monthly_documents_limit: number;
  monthly_documents_used: number;

  created_at: Date;
  updated_at: Date;
  member_count?: number;
  is_organization_complete: boolean;
}

// Team Member
export interface TeamMember {
  _id: string;
  user_id: string;
  organization_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  status: MemberStatus;
  avatar_url?: string;
  invited_by?: string;
  invited_at?: Date;
  joined_at?: Date;
  last_active?: Date;
  last_login_at?: Date;
}

// Notification Preferences
export interface NotificationPreferences {
  user_id: string;
  email_notifications: EmailNotificationSettings;
  webhook_notifications: WebhookNotificationSettings;
  slack_integration?: SlackIntegration;
}

export interface EmailNotificationSettings {
  document_processed: boolean;
  document_failed: boolean;
  api_key_created: boolean;
  api_key_expired: boolean;
  usage_limit_warning: boolean;
  usage_limit_exceeded: boolean;
  team_member_invited: boolean;
  team_member_joined: boolean;
  subscription_updated: boolean;
  payment_failed: boolean;
}

export interface WebhookNotificationSettings {
  enabled: boolean;
  events: string[];
}

export interface SlackIntegration {
  enabled: boolean;
  workspace_name?: string;
  channel?: string;
  webhook_url?: string;
  connected_at?: Date;
}

// Request DTOs
export interface UpdateUserProfileRequest {
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  avatar_url?: string | null;
  job_title?: string;
  department?: string;
  bio?: string;
  country?: string;
}

export interface UpdateOrganizationRequest {
  name?: string;
  logo_url?: string;

  // Company Address
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;

  // Tax Information
  tax_id?: string;

  // Timezone
  timezone?: string;

  // Webhook settings
  webhook_url?: string;
  webhook_secret?: string;
  webhook_events?: string[];
}

export interface InviteTeamMemberRequest {
  email: string;
  role: UserRole;
  message?: string;
}

export interface UpdateTeamMemberRoleRequest {
  role: UserRole;
}

export interface UpdateNotificationPreferencesRequest {
  email_notifications?: Partial<EmailNotificationSettings>;
  webhook_notifications?: Partial<WebhookNotificationSettings>;
  slack_integration?: Partial<SlackIntegration>;
}

export interface Enable2FARequest {
  /** Optional; WiseAuth enrollment is toggled server-side without a TOTP code. */
  code?: string;
}

export interface Disable2FARequest {
  code?: string;
}

// Response DTOs
export interface Enable2FAResponse {
  secret: string;
  qr_code_url: string;
  backup_codes: string[];
}

export interface InviteTeamMemberResponse {
  message: string;
  member?: TeamMember;
  invitation_sent: boolean;
  email?: string;
}

export interface RemoveTeamMemberResponse {
  message: string;
  member_id: string;
}

export interface UploadAvatarResponse {
  avatar_url: string;
}

export interface UploadLogoResponse {
  logo_url: string;
}

